import { HEN_BREEDS } from '../data/breeds';
import { ROOSTER_BREEDS } from '../data/roosters';
import type { EggColor } from '../data/types';
import { buffProduct } from '../core/buffs';
import { nestCapacity } from '../core/economy';
import { isFirstOfKind } from '../core/modifiers';
import type { Critter, Flock, GameEvent, GameState } from '../core/types';
import { boxRect, eventRadius, fieldPoint, inRect, roosterRect, type FarmLayout, type Rect } from './layout';
import { drawParticles } from './particles';
import { background, drawHen, drawRoosterSprite } from './sprites';

export type HitTarget =
  | { kind: 'event'; id: number }
  | { kind: 'critter'; id: number }
  | { kind: 'rooster' }
  | { kind: 'box'; index: number };

export interface RenderFx {
  /** Box index → performance.now() of the last click (hen bounce). */
  bounce: Map<number, number>;
  hover: HitTarget | null;
  theme: string;
}

export const EGG_COLORS: Record<EggColor, string> = {
  brown: '#b86b2e', white: '#f7f4ea', cream: '#f1e2bb', blue: '#9fd6e8', olive: '#8d9d5a',
  chocolate: '#5b331b', speckled: '#c48f5c', dark_speckled: '#8d5b3b', rainbow: '#ff7eb6',
};

const EVENT_STYLE: Record<GameEvent['kind'], { fill: string; glow: string; label: string }> = {
  yolkBurst: { fill: '#fff4c2', glow: 'rgba(255,215,0,0.55)', label: 'YOLK BURST' },
  eggShower: { fill: '#9fd6e8', glow: 'rgba(120,200,255,0.6)', label: 'EGG SHOWER' },
  doubleTime: { fill: '#6fd08c', glow: 'rgba(80,200,120,0.6)', label: '2× LAYING' },
  silver: { fill: '#d9dde3', glow: 'rgba(210,220,235,0.8)', label: 'SILVER' },
  golden: { fill: '#ffd24a', glow: 'rgba(255,200,40,0.85)', label: 'GOLDEN' },
  rotten: { fill: '#6b7b3a', glow: 'rgba(120,140,60,0.6)', label: 'ROTTEN' },
  rainbow: { fill: '#ff7eb6', glow: 'rgba(255,120,200,0.7)', label: 'RAINBOW' },
  roosterEgg: { fill: '#d9a574', glow: 'rgba(90,170,255,0.8)', label: 'ROOSTER EGG' },
  broody: { fill: '#f5deb3', glow: 'rgba(255,200,150,0.5)', label: 'BROODY' },
  broodEgg: { fill: '#fff0d6', glow: 'rgba(255,240,200,0.8)', label: 'BROOD EGG' },
};

// ── Critter motion (pure function of spawn data + sim time) ────────────

function tri(v: number, lo: number, hi: number): number {
  const span = hi - lo;
  const u = (((v - lo) / span) % 2 + 2) % 2;
  return lo + span * (u <= 1 ? u : 2 - u);
}

export function critterPos(c: Critter, simNow: number): { nx: number; ny: number } {
  const t = Math.max(0, (simNow - c.spawnedAt) / 1000);
  switch (c.kind) {
    case 'stormEgg': {
      const drop = Math.min(1, t / 0.6);
      return { nx: c.x, ny: -0.05 + (c.y + 0.05) * drop * drop };
    }
    case 'feather':
      return { nx: tri(c.x + Math.sin(t * 1.3 + c.heading) * 0.05, 0.03, 0.97), ny: tri(c.y * 0.3 + t * 0.04, 0.03, 0.97) };
    case 'butterfly':
      return { nx: tri(c.x + Math.cos(c.heading) * t * 0.06 + Math.sin(t * 3) * 0.02, 0.03, 0.97), ny: tri(c.y + Math.sin(c.heading) * t * 0.05 + Math.cos(t * 4) * 0.03, 0.05, 0.95) };
    case 'bird':
      return { nx: tri(c.x + Math.cos(c.heading) * t * 0.12, 0.03, 0.97), ny: tri(c.y * 0.4 + Math.sin(t * 2) * 0.03, 0.03, 0.6) };
    default: {
      const speed = c.kind === 'chick' ? 0.05 : 0.02;
      const wobble = c.kind === 'chick' ? Math.sin(t * 5) * 0.004 : 0;
      return { nx: tri(c.x + Math.cos(c.heading) * t * speed, 0.03, 0.97), ny: tri(c.y + Math.sin(c.heading) * t * speed * 0.6 + wobble, 0.08, 0.95) };
    }
  }
}

export function critterRadius(l: FarmLayout, c: Critter): number {
  const base = l.grid.boxW * 0.14;
  if (c.kind === 'megaWorm') return base * 1.4;
  if (c.kind === 'bird') return base * 1.3;
  return Math.max(12, base);
}

// ── Hit testing ────────────────────────────────────────────────────────

export function hitTest(l: FarmLayout, s: GameState, flock: Flock, x: number, y: number, simNow: number): HitTarget | null {
  const r = eventRadius(l);
  const events = s.events.filter((e) => e.flockId === flock.id && e.kind !== 'broody');
  for (let i = events.length - 1; i >= 0; i--) {
    const e = events[i]!;
    const p = fieldPoint(l, e.x, e.y);
    if (Math.hypot(p.x - x, p.y - y) <= r * 1.6) return { kind: 'event', id: e.id };
  }
  const critters = s.critters.filter((c) => c.flockId === flock.id);
  for (let i = critters.length - 1; i >= 0; i--) {
    const c = critters[i]!;
    const pos = critterPos(c, simNow);
    const p = fieldPoint(l, pos.nx, pos.ny);
    if (Math.hypot(p.x - x, p.y - y) <= critterRadius(l, c) * 1.5) return { kind: 'critter', id: c.id };
  }
  if (flock.rooster && inRect(roosterRect(l), x, y)) return { kind: 'rooster' };
  for (let i = 0; i < 15; i++) if (inRect(boxRect(l, i), x, y)) return { kind: 'box', index: i };
  return null;
}

// ── Drawing ────────────────────────────────────────────────────────────

function roundRect(ctx: CanvasRenderingContext2D, r: Rect, radius: number): void {
  ctx.beginPath();
  ctx.roundRect(r.x, r.y, r.w, r.h, radius);
}

function pill(ctx: CanvasRenderingContext2D, text: string, cx: number, cy: number, font: number, bg = 'rgba(20,12,6,0.78)', fg = '#fff'): void {
  ctx.font = `bold ${font}px system-ui, sans-serif`;
  const w = ctx.measureText(text).width + font;
  ctx.fillStyle = bg;
  ctx.beginPath();
  ctx.roundRect(cx - w / 2, cy - font * 0.75, w, font * 1.5, font * 0.75);
  ctx.fill();
  ctx.fillStyle = fg;
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(text, cx, cy + 1);
}

/** Truncates text with an ellipsis so it fits maxWidth at the current font. */
function fit(ctx: CanvasRenderingContext2D, text: string, maxWidth: number): string {
  if (ctx.measureText(text).width <= maxWidth) return text;
  let t = text;
  while (t.length > 1 && ctx.measureText(`${t}…`).width > maxWidth) t = t.slice(0, -1);
  return `${t.trimEnd()}…`;
}

function eggShape(ctx: CanvasRenderingContext2D, x: number, y: number, r: number, color: string): void {
  ctx.beginPath();
  ctx.ellipse(x, y, r * 0.78, r, 0, 0, Math.PI * 2);
  ctx.fillStyle = color;
  ctx.fill();
  ctx.strokeStyle = 'rgba(0,0,0,0.35)';
  ctx.lineWidth = 1;
  ctx.stroke();
  ctx.beginPath();
  ctx.ellipse(x - r * 0.25, y - r * 0.35, r * 0.18, r * 0.28, -0.4, 0, Math.PI * 2);
  ctx.fillStyle = 'rgba(255,255,255,0.45)';
  ctx.fill();
}

function drawBackground(ctx: CanvasRenderingContext2D, l: FarmLayout, key: string): void {
  const img = background(key);
  if (img && img.complete && img.naturalWidth) {
    const scale = Math.max(l.width / img.naturalWidth, l.height / img.naturalHeight);
    const w = img.naturalWidth * scale;
    const h = img.naturalHeight * scale;
    ctx.imageSmoothingEnabled = true;
    ctx.drawImage(img, (l.width - w) / 2, (l.height - h) / 2, w, h);
  } else {
    ctx.fillStyle = '#3a2418';
    ctx.fillRect(0, 0, l.width, l.height);
  }
  const g = ctx.createLinearGradient(0, 0, 0, l.height);
  g.addColorStop(0, 'rgba(0,0,0,0.05)');
  g.addColorStop(1, 'rgba(0,0,0,0.35)');
  ctx.fillStyle = g;
  ctx.fillRect(0, 0, l.width, l.height);
}

function drawPen(ctx: CanvasRenderingContext2D, l: FarmLayout, s: GameState, flock: Flock, now: number, fx: RenderFx): void {
  ctx.fillStyle = 'rgba(25,15,8,0.62)';
  roundRect(ctx, l.pen, 12);
  ctx.fill();
  const rr = roosterRect(l);
  const textX = rr.x + rr.w + l.font;
  ctx.textAlign = 'left';
  ctx.textBaseline = 'alphabetic';
  if (!flock.rooster) {
    ctx.fillStyle = 'rgba(255,255,255,0.8)';
    ctx.font = `bold ${l.font * 1.2}px system-ui, sans-serif`;
    ctx.fillText('No rooster yet', l.pen.x + l.font, l.pen.y + l.pen.h * 0.42);
    ctx.font = `${l.font}px system-ui, sans-serif`;
    ctx.fillStyle = 'rgba(255,255,255,0.6)';
    ctx.fillText(fit(ctx, 'A rooster boosts this flock and crows for bursts of laying.', l.pen.w - l.font * 2), l.pen.x + l.font, l.pen.y + l.pen.h * 0.66);
    return;
  }
  const r = flock.rooster;
  const rb = ROOSTER_BREEDS[r.breed];
  const crowing = buffProduct(flock.crowBuffs, s.time) > 1;
  const ready = s.time >= r.crowReadyAt;
  if (ready) {
    const pulse = 0.5 + 0.5 * Math.sin(now / 250);
    ctx.save();
    ctx.shadowColor = `rgba(255,210,70,${0.45 + 0.4 * pulse})`;
    ctx.shadowBlur = 18;
    ctx.fillStyle = 'rgba(255,210,70,0.12)';
    ctx.beginPath();
    ctx.arc(rr.x + rr.w / 2, rr.y + rr.h / 2, rr.w * 0.48, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }
  const hover = fx.hover?.kind === 'rooster';
  const frame = crowing ? 6 : Math.floor(now / 900) % 2;
  const lift = hover ? -3 : 0;
  drawRoosterSprite(ctx, r.breed, r.variant, frame, rr.x, rr.y + lift, rr.w);
  const textW = l.pen.x + l.pen.w - textX - l.font;
  ctx.fillStyle = '#fff';
  ctx.font = `bold ${l.font * 1.25}px system-ui, sans-serif`;
  ctx.fillText(fit(ctx, r.name, textW), textX, l.pen.y + l.pen.h * 0.3);
  ctx.font = `${l.font}px system-ui, sans-serif`;
  ctx.fillStyle = 'rgba(255,255,255,0.75)';
  ctx.fillText(fit(ctx, `${rb.name} rooster · egg value ×${rb.aura.toFixed(2)} in this flock`, textW), textX, l.pen.y + l.pen.h * 0.5);
  // Crow status bar.
  const barX = textX;
  const barY = l.pen.y + l.pen.h * 0.64;
  const barW = Math.min(l.pen.w - (textX - l.pen.x) - l.font, l.pen.w * 0.55);
  const barH = Math.max(8, l.font * 0.8);
  ctx.fillStyle = 'rgba(255,255,255,0.12)';
  ctx.beginPath();
  ctx.roundRect(barX, barY, barW, barH, barH / 2);
  ctx.fill();
  let label: string;
  if (crowing) {
    const left = Math.max(...flock.crowBuffs.map((b) => b.until)) - s.time;
    ctx.fillStyle = '#ffcf40';
    ctx.beginPath();
    ctx.roundRect(barX, barY, barW * Math.min(1, left / (rb.crowDurationSec * 1000)), barH, barH / 2);
    ctx.fill();
    label = `Crowing! ×${rb.crowPower} laying — ${Math.ceil(left / 1000)}s`;
  } else if (ready) {
    ctx.fillStyle = '#7ccf5a';
    ctx.beginPath();
    ctx.roundRect(barX, barY, barW, barH, barH / 2);
    ctx.fill();
    label = 'Crow ready — click the rooster!';
  } else {
    const total = rb.crowCooldownSec * 1000;
    const left = r.crowReadyAt - s.time;
    ctx.fillStyle = '#8fa8c0';
    ctx.beginPath();
    ctx.roundRect(barX, barY, barW * Math.max(0, Math.min(1, 1 - left / total)), barH, barH / 2);
    ctx.fill();
    const m = Math.floor(left / 60000);
    const sec = Math.ceil((left % 60000) / 1000);
    label = `Next crow in ${m}:${String(sec).padStart(2, '0')}`;
  }
  ctx.fillStyle = 'rgba(255,255,255,0.85)';
  ctx.font = `${l.font * 0.9}px system-ui, sans-serif`;
  ctx.fillText(fit(ctx, label, textW), barX, barY + barH + l.font * 1.1);
}

function drawBoxes(ctx: CanvasRenderingContext2D, l: FarmLayout, s: GameState, flock: Flock, now: number, fx: RenderFx): void {
  const broody = new Map<number, GameEvent>();
  for (const e of s.events) if (e.kind === 'broody' && e.flockId === flock.id && e.boxIndex !== undefined) broody.set(e.boxIndex, e);
  for (let i = 0; i < 15; i++) {
    const r = boxRect(l, i);
    const box = flock.boxes[i];
    const hover = fx.hover?.kind === 'box' && fx.hover.index === i;
    if (!box) {
      ctx.setLineDash([6, 6]);
      ctx.strokeStyle = i === flock.boxes.length ? 'rgba(255,255,255,0.35)' : 'rgba(255,255,255,0.12)';
      ctx.lineWidth = 2;
      roundRect(ctx, r, 10);
      ctx.stroke();
      ctx.setLineDash([]);
      if (i === flock.boxes.length) {
        ctx.fillStyle = 'rgba(255,255,255,0.45)';
        ctx.font = `bold ${l.font * 1.6}px system-ui, sans-serif`;
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText('+', r.x + r.w / 2, r.y + r.h / 2 - l.font * 0.4);
        ctx.font = `${l.font * 0.8}px system-ui, sans-serif`;
        ctx.fillText('nesting box', r.x + r.w / 2, r.y + r.h / 2 + l.font);
      }
      continue;
    }
    // Nest box body.
    ctx.fillStyle = hover ? 'rgba(90,60,30,0.85)' : 'rgba(70,45,24,0.78)';
    roundRect(ctx, r, 10);
    ctx.fill();
    ctx.strokeStyle = 'rgba(255,220,160,0.18)';
    ctx.lineWidth = 1.5;
    ctx.stroke();
    // Straw.
    ctx.fillStyle = 'rgba(214,170,90,0.55)';
    ctx.beginPath();
    ctx.ellipse(r.x + r.w / 2, r.y + r.h * 0.8, r.w * 0.4, r.h * 0.1, 0, 0, Math.PI * 2);
    ctx.fill();
    const hen = box.hen;
    if (!hen) {
      ctx.fillStyle = 'rgba(255,255,255,0.55)';
      ctx.font = `${l.font * 0.85}px system-ui, sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText('Pick a hen', r.x + r.w / 2, r.y + r.h * 0.45);
      continue;
    }
    const breed = HEN_BREEDS[hen.breed];
    const isBroody = hen.broodySince !== null;
    const bounceStart = fx.bounce.get(i);
    const bounceT = bounceStart === undefined ? 1 : (now - bounceStart) / 300;
    const bounce = bounceT < 1 ? Math.sin(bounceT * Math.PI) : 0;
    let frame = Math.floor((now + i * 377) / 1000) % 2;
    if ((Math.floor((now + i * 911) / 3000) % 4) === 0 && ((now + i * 911) % 3000) < 400) frame = ((now + i * 911) % 3000) < 200 ? 2 : 3;
    if (bounceT < 1) frame = 7;
    if (isBroody) frame = 7;
    const size = l.henSize * (1 + bounce * 0.12);
    const hx = r.x + (r.w - size) / 2;
    const hy = r.y + r.h * 0.04 - bounce * 8;
    ctx.save();
    if (isBroody) ctx.filter = 'brightness(0.75) saturate(0.8)';
    drawHen(ctx, hen.breed, hen.variant, frame, hx, hy, size);
    ctx.restore();
    // First-of-kind star.
    if (isFirstOfKind(flock, hen)) {
      ctx.fillStyle = '#ffd24a';
      ctx.font = `${l.font}px system-ui, sans-serif`;
      ctx.textAlign = 'left';
      ctx.textBaseline = 'top';
      ctx.fillText('★', r.x + 5, r.y + 4);
    }
    // Eggs.
    const eggR = Math.max(5, r.w * 0.065);
    const shown = Math.min(box.eggs, 6);
    const eggY = r.y + r.h * 0.74;
    const color = EGG_COLORS[breed.eggColor];
    for (let e = 0; e < shown; e++) {
      const ex = r.x + r.w / 2 + (e - (shown - 1) / 2) * eggR * 1.7;
      eggShape(ctx, ex, eggY, eggR, breed.eggColor === 'rainbow' ? `hsl(${(e * 60 + now / 20) % 360} 70% 70%)` : color);
    }
    const cap = nestCapacity(box);
    if (box.eggs > 6) pill(ctx, `${box.eggs}`, r.x + r.w - l.font * 1.2, eggY, l.font * 0.8);
    if (box.eggs >= cap) pill(ctx, 'FULL', r.x + r.w / 2, r.y + r.h * 0.6, l.font * 0.75, 'rgba(190,60,40,0.9)');
    // Lay progress bar (a bar, not a ghost egg).
    if (!isBroody) {
      const bw = r.w * 0.7;
      const bx = r.x + (r.w - bw) / 2;
      const by = r.y + r.h * 0.84;
      ctx.fillStyle = 'rgba(0,0,0,0.35)';
      ctx.fillRect(bx, by, bw, 3);
      ctx.fillStyle = box.eggs >= cap ? 'rgba(200,80,60,0.9)' : 'rgba(255,220,120,0.9)';
      ctx.fillRect(bx, by, bw * Math.min(1, hen.progress), 3);
    }
    // Name.
    ctx.fillStyle = 'rgba(255,255,255,0.88)';
    ctx.font = `${Math.max(10, l.font * 0.75)}px system-ui, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'alphabetic';
    const short = r.w < 120 && breed.name.length > 12 ? breed.name.split(' ').slice(-1)[0]! : breed.name;
    ctx.fillText(fit(ctx, short, r.w - 6), r.x + r.w / 2, r.y + r.h - 5);
    // Broody overlay.
    const ev = broody.get(i);
    if (isBroody && ev) {
      ctx.fillStyle = 'rgba(40,20,10,0.35)';
      roundRect(ctx, r, 10);
      ctx.fill();
      if (ev.broodUntil) {
        const total = ev.broodUntil - (ev.decideBy ?? ev.spawnedAt);
        const frac = Math.max(0, Math.min(1, 1 - (ev.broodUntil - s.time) / Math.max(1, total)));
        ctx.strokeStyle = '#ffcf8a';
        ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.arc(r.x + r.w / 2, r.y + r.h * 0.45, r.w * 0.36, -Math.PI / 2, -Math.PI / 2 + frac * Math.PI * 2);
        ctx.stroke();
        pill(ctx, `🪺 ${Math.ceil((ev.broodUntil - s.time) / 60000)}m`, r.x + r.w / 2, r.y + r.h * 0.62, l.font * 0.8);
      } else {
        const pulse = 0.6 + 0.4 * Math.sin(now / 200);
        pill(ctx, '🪺 Broody!', r.x + r.w / 2, r.y + r.h * 0.6, l.font * 0.85, `rgba(200,110,40,${pulse})`);
      }
    }
  }
}

function drawEvent(ctx: CanvasRenderingContext2D, l: FarmLayout, s: GameState, e: GameEvent, now: number, hover: boolean): void {
  const style = EVENT_STYLE[e.kind];
  const p = fieldPoint(l, e.x, e.y);
  const r = eventRadius(l) * (hover ? 1.12 : 1);
  const pulse = 1 + 0.08 * Math.sin(now / 160);
  const age = s.time - e.spawnedAt;
  ctx.save();
  ctx.shadowColor = style.glow;
  ctx.shadowBlur = 18 * pulse;
  let fill = style.fill;
  if (e.kind === 'rainbow') fill = `hsl(${(now / 8) % 360} 80% 65%)`;
  if (e.bloom) fill = '#ff9ecf';
  // Storm/drop-in for the first 250 ms.
  const scale = Math.min(1, age / 250) * pulse;
  eggShape(ctx, p.x, p.y, r * scale, fill);
  ctx.restore();
  if (e.kind === 'golden' || e.kind === 'silver') {
    ctx.strokeStyle = e.kind === 'golden' ? 'rgba(255,240,170,0.9)' : 'rgba(255,255,255,0.9)';
    ctx.lineWidth = 1.5;
    for (let k = 0; k < 4; k++) {
      const a = now / 600 + (k * Math.PI) / 2;
      ctx.beginPath();
      ctx.moveTo(p.x + Math.cos(a) * r * 1.3, p.y + Math.sin(a) * r * 1.3);
      ctx.lineTo(p.x + Math.cos(a) * r * 1.6, p.y + Math.sin(a) * r * 1.6);
      ctx.stroke();
    }
  }
  // Remaining-time ring.
  if (Number.isFinite(e.expiresAt) && !e.incubateUntil) {
    const total = e.expiresAt - e.spawnedAt;
    const frac = Math.max(0, (e.expiresAt - s.time) / total);
    ctx.strokeStyle = frac < 0.25 ? 'rgba(255,90,70,0.9)' : 'rgba(255,255,255,0.7)';
    ctx.lineWidth = 2.5;
    ctx.beginPath();
    ctx.arc(p.x, p.y, r * 1.35, -Math.PI / 2, -Math.PI / 2 + frac * Math.PI * 2);
    ctx.stroke();
  }
  let label = e.bloom ? 'BLOOM' : style.label;
  if (e.kind === 'roosterEgg' && e.incubateUntil) {
    const left = Math.max(0, e.incubateUntil - s.time);
    label = `HATCHING ${Math.floor(left / 60000)}:${String(Math.ceil((left % 60000) / 1000)).padStart(2, '0')}`;
  } else if (e.kind === 'roosterEgg' || (age < 2500 && e.kind !== 'broodEgg')) {
    const float = Math.min(1, age / 2500);
    ctx.globalAlpha = e.kind === 'roosterEgg' ? 1 : 1 - float;
    pill(ctx, 'Click!', p.x, p.y - r * 2.4 - float * 10, l.font * 0.8, 'rgba(255,90,40,0.9)');
    ctx.globalAlpha = 1;
  }
  pill(ctx, label, p.x, p.y + r * 1.9, l.font * 0.72);
}

function drawCritter(ctx: CanvasRenderingContext2D, l: FarmLayout, c: Critter, simNow: number, now: number): void {
  const pos = critterPos(c, simNow);
  const p = fieldPoint(l, pos.nx, pos.ny);
  const r = critterRadius(l, c);
  const flap = Math.sin(now / 90);
  ctx.save();
  switch (c.kind) {
    case 'chick':
      ctx.fillStyle = '#ffe14d';
      ctx.beginPath();
      ctx.arc(p.x, p.y, r * 0.8, 0, Math.PI * 2);
      ctx.fill();
      ctx.beginPath();
      ctx.arc(p.x + r * 0.55, p.y - r * 0.45, r * 0.45, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = '#ff9800';
      ctx.beginPath();
      ctx.moveTo(p.x + r * 0.95, p.y - r * 0.45);
      ctx.lineTo(p.x + r * 1.3, p.y - r * 0.35);
      ctx.lineTo(p.x + r * 0.95, p.y - r * 0.25);
      ctx.fill();
      ctx.fillStyle = '#222';
      ctx.fillRect(p.x + r * 0.62, p.y - r * 0.6, 2, 2);
      break;
    case 'feather': {
      const color = c.breed ? EGG_COLORS[HEN_BREEDS[c.breed].eggColor] : '#eee';
      ctx.translate(p.x, p.y);
      ctx.rotate(Math.sin(now / 500 + c.id) * 0.6);
      ctx.fillStyle = color;
      ctx.beginPath();
      ctx.ellipse(0, 0, r * 0.3, r, 0, 0, Math.PI * 2);
      ctx.fill();
      ctx.strokeStyle = 'rgba(0,0,0,0.4)';
      ctx.beginPath();
      ctx.moveTo(0, -r);
      ctx.lineTo(0, r * 1.2);
      ctx.stroke();
      break;
    }
    case 'worm':
    case 'megaWorm':
      ctx.fillStyle = c.kind === 'megaWorm' ? '#e0457b' : '#f08fb0';
      for (let k = 0; k < 4; k++) {
        ctx.beginPath();
        ctx.arc(p.x - k * r * 0.45, p.y + Math.sin(now / 120 + k) * r * 0.15, r * (0.42 - k * 0.04), 0, Math.PI * 2);
        ctx.fill();
      }
      if (c.kind === 'megaWorm') pill(ctx, '♥'.repeat(c.hp), p.x, p.y - r * 1.2, l.font * 0.7, 'rgba(0,0,0,0.6)', '#ff6f91');
      break;
    case 'butterfly':
      ctx.fillStyle = '#ff7ac8';
      ctx.beginPath();
      ctx.ellipse(p.x - r * 0.4, p.y, r * 0.5, r * (0.6 + 0.3 * flap), 0.3, 0, Math.PI * 2);
      ctx.ellipse(p.x + r * 0.4, p.y, r * 0.5, r * (0.6 + 0.3 * flap), -0.3, 0, Math.PI * 2);
      ctx.fill();
      ctx.fillStyle = '#333';
      ctx.fillRect(p.x - 1, p.y - r * 0.5, 2, r);
      break;
    case 'bird':
      ctx.strokeStyle = '#2b2b2b';
      ctx.lineWidth = 3;
      ctx.beginPath();
      ctx.moveTo(p.x - r, p.y - r * 0.4 * flap);
      ctx.quadraticCurveTo(p.x - r * 0.4, p.y - r * 0.6, p.x, p.y);
      ctx.quadraticCurveTo(p.x + r * 0.4, p.y - r * 0.6, p.x + r, p.y - r * 0.4 * flap);
      ctx.stroke();
      break;
    case 'stormEgg':
      ctx.shadowColor = 'rgba(255,210,70,0.9)';
      ctx.shadowBlur = 12;
      eggShape(ctx, p.x, p.y, r * 0.85, '#ffd24a');
      break;
  }
  ctx.restore();
}

export function drawFarm(
  ctx: CanvasRenderingContext2D, l: FarmLayout, s: GameState, flock: Flock, simNow: number, now: number, fx: RenderFx,
): void {
  drawBackground(ctx, l, flock.background === 'theme' ? fx.theme : flock.background);
  drawPen(ctx, l, s, flock, now, fx);
  drawBoxes(ctx, l, s, flock, now, fx);
  for (const e of s.events) {
    if (e.flockId !== flock.id || e.kind === 'broody') continue;
    drawEvent(ctx, l, s, e, now, fx.hover?.kind === 'event' && fx.hover.id === e.id);
  }
  for (const c of s.critters) if (c.flockId === flock.id) drawCritter(ctx, l, c, simNow, now);
  drawParticles(ctx, now);
}
